//src/pages/cadastros/GroupsPage.tsx
import { useEffect, useMemo, useState } from "react";
import { KeyRound, Plus, RefreshCw, Search, Shield, Users } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { CrudCardActions } from "@/components/shared/CrudCardActions";
import { EmptyState } from "@/components/shared/EmptyState";
import { PageHeader } from "@/components/shared/PageHeader";
import { PermissionButton } from "@/components/shared/PermissionButton";
import { useAuth } from "@/contexts/AuthContext";
import {
  useCreateGroup,
  useDeleteGroup,
  useGroupDetail,
  useGroups,
  usePatchGroupStatus,
  usePermissionsCatalog,
  useUpdateGroup,
  useUpdateGroupPermissions,
} from "@/hooks/useGroups";
import { PERMISSIONS } from "@/lib/permissions";
import type { GroupListItem, GroupScope } from "@/services/groups";

const EMPTY_FORM = {
  name: "",
  description: "",
  scope: "tenant" as GroupScope,
  is_active: true,
};

function normalize(value: string) {
  return (value || "").trim().toLocaleLowerCase("pt-BR");
}

function scopeLabel(scope: GroupScope) {
  if (scope === "client") return "Perfil de Cliente";
  if (scope === "platform") return "Grupo Normex";
  return "Grupo da Empresa";
}


function errorMessage(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback;
}

export default function GroupsPage() {
  const { user } = useAuth();
  const [scope, setScope] = useState<GroupScope>("tenant");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [search, setSearch] = useState("");
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<GroupListItem | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);
  const [permissionTarget, setPermissionTarget] = useState<GroupListItem | null>(null);
  const [permissionSearch, setPermissionSearch] = useState("");
  const [selectedPermissionIds, setSelectedPermissionIds] = useState<string[]>([]);
  const [confirmTarget, setConfirmTarget] = useState<GroupListItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<GroupListItem | null>(null);

  const permissions = user?.permissions ?? [];
  const canWrite = permissions.includes(PERMISSIONS.TENANT_GROUPS_WRITE);
  const canReadCatalog = permissions.includes(PERMISSIONS.TENANT_PERMISSIONS_READ);

  const groupsQ = useGroups(scope, includeInactive);
  const detailQ = useGroupDetail(permissionDialogOpen ? permissionTarget?.id : undefined);
  const permissionsQ = usePermissionsCatalog(permissionTarget?.scope, permissionDialogOpen && canReadCatalog);
  const createMut = useCreateGroup();
  const updateMut = useUpdateGroup();
  const statusMut = usePatchGroupStatus();
  const deleteMut = useDeleteGroup();
  const permissionsMut = useUpdateGroupPermissions();

  const busy = createMut.isPending || updateMut.isPending || statusMut.isPending || deleteMut.isPending || permissionsMut.isPending;
  const groups = useMemo(() => {
    const query = normalize(search);
    return (groupsQ.data ?? []).filter((item) => {
      if (!query) return true;
      return normalize(item.name).includes(query) || normalize(item.description || "").includes(query);
    });
  }, [groupsQ.data, search]);

  const catalog = useMemo(() => {
    const query = normalize(permissionSearch);
    return (permissionsQ.data ?? []).filter((item) => {
      if (!query) return true;
      return normalize(item.code).includes(query) || normalize(item.description || "").includes(query);
    });
  }, [permissionSearch, permissionsQ.data]);

  useEffect(() => {
    if (detailQ.data && permissionDialogOpen) {
      setSelectedPermissionIds(detailQ.data.permission_ids ?? []);
    }
  }, [detailQ.data, permissionDialogOpen]);

  function openCreate() {
    setEditingGroup(null);
    setForm({ ...EMPTY_FORM, scope });
    setGroupDialogOpen(true);
  }

  function openEdit(group: GroupListItem) {
    setEditingGroup(group);
    setForm({
      name: group.name,
      description: group.description || "",
      scope: group.scope,
      is_active: group.is_active,
    });
    setGroupDialogOpen(true);
  }

  function openPermissions(group: GroupListItem) {
    setPermissionTarget(group);
    setSelectedPermissionIds([]);
    setPermissionSearch("");
    setPermissionDialogOpen(true);
  }

  async function saveGroup() {
    if (!form.name.trim()) {
      toast.error("Informe o nome do grupo.");
      return;
    }
    try {
      const body = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        scope: form.scope,
        is_active: form.is_active,
      };
      if (editingGroup) {
        await updateMut.mutateAsync({ id: editingGroup.id, body });
        toast.success("Grupo atualizado.");
      } else {
        await createMut.mutateAsync(body);
        toast.success(scope === "client" ? "Perfil de Cliente criado." : "Grupo da Empresa criado.");
      }
      setGroupDialogOpen(false);
    } catch (error: unknown) {
      const message = errorMessage(error, "Erro ao salvar grupo.");
      toast.error(message === "group_name_already_exists" ? "Já existe um grupo com este nome." : message);
    }
  }

  async function savePermissions() {
    if (!permissionTarget) return;
    try {
      await permissionsMut.mutateAsync({
        id: permissionTarget.id,
        body: { permission_ids: selectedPermissionIds },
      });
      toast.success("Permissões atualizadas.");
      setPermissionDialogOpen(false);
    } catch (error: unknown) {
      toast.error(errorMessage(error, "Erro ao atualizar permissões."));
    }
  }

  async function toggleStatus() {
    if (!confirmTarget) return;
    try {
      await statusMut.mutateAsync({
        id: confirmTarget.id,
        body: { is_active: !confirmTarget.is_active },
      });
      toast.success(confirmTarget.is_active ? "Grupo inativado." : "Grupo ativado.");
      setConfirmTarget(null);
    } catch (error: unknown) {
      toast.error(errorMessage(error, "Erro ao alterar status."));
    }
  }

  async function deleteGroup() {
    if (!deleteTarget) return;
    try {
      await deleteMut.mutateAsync(deleteTarget.id);
      toast.success("Grupo excluído.");
      setDeleteTarget(null);
    } catch (error: unknown) {
      const message = errorMessage(error, "Erro ao excluir grupo.");
      toast.error(message === "group_is_in_use" ? "Este grupo está vinculado a usuários e não pode ser excluído." : message);
    }
  }

  const visibleIds = catalog.map((item) => item.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedPermissionIds.includes(id));

  return (
    <div>
      <PageHeader
        title="Grupos de Acesso"
        description="Separe os acessos administrativos da Empresa dos perfis operacionais usados nos Clientes."
      >
        <PermissionButton permission={PERMISSIONS.TENANT_GROUPS_WRITE} onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          {scope === "client" ? "Novo Perfil de Cliente" : "Novo Grupo da Empresa"}
        </PermissionButton>
      </PageHeader>

      <Tabs value={scope} onValueChange={(value) => setScope(value as GroupScope)} className="mb-4">
        <TabsList>
          <TabsTrigger value="tenant">Grupos da Empresa</TabsTrigger>
          <TabsTrigger value="client">Perfis de Cliente</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card className="mb-4">
        <CardContent className="flex flex-col gap-3 p-4 md:flex-row md:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder={scope === "client" ? "Buscar perfil de Cliente..." : "Buscar grupo da Empresa..."}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <Button variant="outline" onClick={() => setIncludeInactive((value) => !value)}>
            {includeInactive ? "Ocultar inativos" : "Mostrar inativos"}
          </Button>
          <Button variant="outline" onClick={() => void groupsQ.refetch()} disabled={groupsQ.isFetching}>
            <RefreshCw className="mr-2 h-4 w-4" /> Atualizar
          </Button>
        </CardContent>
      </Card>

      {groupsQ.isLoading ? (
        <Card className="p-6 text-sm text-muted-foreground">Carregando grupos...</Card>
      ) : groupsQ.isError ? (
        <Card className="p-6 text-sm text-destructive">Erro ao carregar grupos.</Card>
      ) : groups.length === 0 ? (
        <Card>
          <EmptyState
            icon={Shield}
            title={scope === "client" ? "Nenhum perfil de Cliente cadastrado" : "Nenhum grupo da Empresa cadastrado"}
            description={scope === "client" ? "Crie perfis como Administrador, Operador e Consulta para atribuir em cada Cliente." : "Crie os grupos que poderão acessar o Portal da Empresa."}
            actionLabel={canWrite ? (scope === "client" ? "Criar perfil" : "Criar grupo") : undefined}
            onAction={canWrite ? openCreate : undefined}
          />
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {groups.map((group) => (
            <Card key={group.id} className={!group.is_active ? "opacity-70" : undefined}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Shield className="h-4 w-4 shrink-0 text-primary" />
                      <span className="truncate">{group.name}</span>
                    </CardTitle>
                    <Badge className="mt-2" variant={group.scope === "client" ? "secondary" : "outline"}>
                      {scopeLabel(group.scope)}
                    </Badge>
                  </div>
                  <CrudCardActions
                    onPermissions={canReadCatalog ? () => openPermissions(group) : undefined}
                    onEdit={() => openEdit(group)}
                    onToggleActive={() => setConfirmTarget(group)}
                    onDelete={() => setDeleteTarget(group)}
                    isActive={group.is_active}
                    disabled={busy}
                    permissionsPermission={PERMISSIONS.TENANT_GROUPS_WRITE}
                    editPermission={PERMISSIONS.TENANT_GROUPS_WRITE}
                    toggleActivePermission={PERMISSIONS.TENANT_GROUPS_WRITE}
                    deletePermission={PERMISSIONS.TENANT_GROUPS_WRITE}
                  />
                </div>
              </CardHeader>
              <CardContent>
                <p className="min-h-10 text-sm text-muted-foreground">{group.description || "Sem descrição."}</p>
                <div className="mt-4 flex gap-4 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {group.user_count} usuário(s)</span>
                  <span className="inline-flex items-center gap-1"><KeyRound className="h-3.5 w-3.5" /> {group.permission_count} permissão(ões)</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={groupDialogOpen} onOpenChange={setGroupDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingGroup ? "Editar grupo" : scope === "client" ? "Novo Perfil de Cliente" : "Novo Grupo da Empresa"}</DialogTitle>
            <DialogDescription>
              {form.scope === "client"
                ? "Este perfil será reutilizado para definir o acesso do usuário em um ou mais Clientes."
                : "Este grupo controla somente os menus administrativos do Portal da Empresa."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Tipo</Label>
              <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm font-medium">{scopeLabel(form.scope)}</div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="group-name">Nome</Label>
              <Input id="group-name" value={form.name} onChange={(event) => setForm((value) => ({ ...value, name: event.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="group-description">Descrição</Label>
              <Textarea id="group-description" value={form.description} onChange={(event) => setForm((value) => ({ ...value, description: event.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGroupDialogOpen(false)}>Cancelar</Button>
            <Button onClick={() => void saveGroup()} disabled={busy}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={permissionDialogOpen} onOpenChange={setPermissionDialogOpen}>
        <DialogContent className="max-h-[86vh] max-w-3xl overflow-hidden">
          <DialogHeader>
            <DialogTitle>Permissões — {permissionTarget?.name}</DialogTitle>
            <DialogDescription>
              São exibidas somente permissões do contexto <strong>{permissionTarget ? scopeLabel(permissionTarget.scope) : ""}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 overflow-hidden">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input className="pl-9" placeholder="Buscar permissão..." value={permissionSearch} onChange={(event) => setPermissionSearch(event.target.value)} />
            </div>
            <label className="flex items-center gap-2 rounded-md border p-3 text-sm font-medium">
              <Checkbox
                checked={allVisibleSelected}
                onCheckedChange={(checked) => {
                  if (checked) setSelectedPermissionIds((current) => Array.from(new Set([...current, ...visibleIds])));
                  else setSelectedPermissionIds((current) => current.filter((id) => !visibleIds.includes(id)));
                }}
              />
              Selecionar todas as permissões exibidas
            </label>
            <div className="max-h-[48vh] space-y-2 overflow-y-auto pr-1">
              {permissionsQ.isLoading || detailQ.isLoading ? (
                <div className="p-6 text-center text-sm text-muted-foreground">Carregando permissões...</div>
              ) : catalog.map((permission) => (
                <label key={permission.id} className="flex cursor-pointer items-start gap-3 rounded-md border p-3 hover:bg-muted/40">
                  <Checkbox
                    className="mt-0.5"
                    checked={selectedPermissionIds.includes(permission.id)}
                    onCheckedChange={() => setSelectedPermissionIds((current) => current.includes(permission.id) ? current.filter((id) => id !== permission.id) : [...current, permission.id])}
                  />
                  <span>
                    <span className="block text-sm font-medium">{permission.description || permission.code}</span>
                    <code className="mt-1 block text-xs text-muted-foreground">{permission.code}</code>
                  </span>
                </label>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPermissionDialogOpen(false)}>Cancelar</Button>
            <Button onClick={() => void savePermissions()} disabled={busy}>Salvar permissões</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirmTarget}
        onOpenChange={(open) => !open && setConfirmTarget(null)}
        title={confirmTarget?.is_active ? "Inativar grupo?" : "Ativar grupo?"}
        description="A alteração afeta os usuários vinculados a este grupo."
        confirmLabel={confirmTarget?.is_active ? "Inativar" : "Ativar"}
        onConfirm={() => void toggleStatus()}
      />
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Excluir grupo?"
        description="A exclusão só será permitida quando o grupo não estiver vinculado a usuários."
        onConfirm={() => void deleteGroup()}
      />
    </div>
  );
}
